/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { InputAdornment, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { StringProp } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import useLocalState from 'hooks/useLocalState';
import Icon from 'icons/Icon';
import React, { memo, useState } from 'react';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  tip: {
    cursor: 'default',
  },
});

function TextProperty({
  appUid,
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<StringProp, string>) {
  const classes = useStyles();
  const [undoable, setUndoable] = useState(true);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const [localValue, setLocalValue] = useLocalState(value, (val) => {
    onChange(property, val, undoable);
    setUndoable(false);
  });
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <TextField
      disabled={isViewOnlyUser}
      autoComplete="off"
      className={classes.root}
      fullWidth
      label={def.label}
      variant="standard"
      required={required}
      value={localValue ?? ''}
      multiline={def.multiline}
      placeholder={def.placeholder}
      data-testid={property}
      // minRows={def.multiline ? 3 : undefined}
      maxRows={def.multiline ? 15 : undefined}
      onFocus={(e) => {
        setUndoable(true);
        e.target.select();
      }}
      onBlur={(e) => {
        setUndoable(true);
      }}
      classes={{ root: classes.input }}
      onChange={(e) => {
        setLocalValue(e.target.value);
      }}
      InputProps={{
        endAdornment: helpText ? (
          <InputAdornment position="end">
            <Icon
              disabled={isViewOnlyUser}
              className={classes.tip}
              iconName="comment-exclamation"
              data-tip={helpText}
              data-tip-delay={0}
              data-tip-at="top"
              data-tip-markdown
            />
          </InputAdornment>
        ) : undefined,
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={{
        maxLength: def.maxLength,
        'data-testid': `${property}-input`,
      }}
    />
  );
}

export default memo(TextProperty);
